// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
// Pure camera arithmetic — no DOM. The view controller (view.js) owns the live
// <svg viewBox> and the element's clientWidth; these functions compute the
// numbers it applies, so the maths stays unit-testable without a browser.
//
// Scale convention: scale = clientWidth / viewBox.w  (pixels per logical unit).
// Zoom convention:  scale = baseScale * 2 ** zoom, where baseScale is the
// fit-to-content scale (zoom 0 == fitted). This matches the 2 ** branchZoom
// unit already used by the branch/step scaling in scales.js.

/** Viewbox window width that renders content at `scale`. */
export function widthForScale(clientWidth, scale) {
  return clientWidth / scale;
}

/** Scale produced by a viewbox window of the given `width`. */
export function scaleForWidth(clientWidth, width) {
  return clientWidth / width;
}

/** Absolute scale for a zoom level, relative to the fitted `baseScale`. */
export function scaleForZoom(baseScale, zoom) {
  return baseScale * 2 ** zoom;
}

/** Zoom level of an absolute scale, relative to the fitted `baseScale`. */
export function zoomForScale(baseScale, scale) {
  return Math.log2(scale / baseScale);
}

/** Bound `value` to the inclusive range [min, max]. */
export function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/**
 * Recompute a viewBox so its window becomes (newW x newH) while the normalized
 * anchor [fx, fy] in [0,1] stays over the same logical point — i.e. zoom about
 * that anchor. Defaults to the centre. Pure: no element access.
 */
export function recentreViewBox(vb, newW, newH, anchor = [0.5, 0.5]) {
  const [fx, fy] = anchor;
  const anchorX = vb.x + fx * vb.w;
  const anchorY = vb.y + fy * vb.h;
  return {
    x: anchorX - fx * newW,
    y: anchorY - fy * newH,
    w: newW,
    h: newH,
  };
}

/**
 * Scale bounds implied by viewbox-width bounds. A smaller window means a larger
 * scale, so the bounds invert: minScale comes from maxW, maxScale from minW.
 */
export function scaleBounds(clientWidth, minW, maxW) {
  return {
    minScale: clientWidth / maxW,
    maxScale: clientWidth / minW,
  };
}

/**
 * Project a viewBox-space (world) point to client (screen-pixel) coordinates,
 * given the live viewBox window `vb` and the svg's bounding `rect`. Inverse of
 * {@link clientToWorld}; the world→client direction backing projectPoint().
 *
 * @param {{x:number,y:number,w:number,h:number}} vb
 * @param {{left:number,top:number,width:number,height:number}} rect
 * @param {[number, number]} point - World coordinates [wx, wy].
 * @returns {[number, number]} Client coordinates [cx, cy].
 */
export function worldToClient(vb, rect, [wx, wy]) {
  const fx = (wx - vb.x) / vb.w;
  const fy = (wy - vb.y) / vb.h;
  return [rect.left + fx * rect.width, rect.top + fy * rect.height];
}

/**
 * Unproject a client (screen-pixel) point back to viewBox-space (world)
 * coordinates. Inverse of {@link worldToClient} and the pure form of view.js's
 * clientToViewBox; the client→world direction backing unprojectPoint().
 *
 * @param {{x:number,y:number,w:number,h:number}} vb
 * @param {{left:number,top:number,width:number,height:number}} rect
 * @param {[number, number]} point - Client coordinates [cx, cy].
 * @returns {[number, number]} World coordinates [wx, wy].
 */
export function clientToWorld(vb, rect, [cx, cy]) {
  const fx = (cx - rect.left) / rect.width;
  const fy = (cy - rect.top) / rect.height;
  return [vb.x + fx * vb.w, vb.y + fy * vb.h];
}
